import React from 'react';
import { Loader2, Fuel, AlertTriangle, CheckCircle } from 'lucide-react';

const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const eth = (v) => `${Number(v || 0).toFixed(6)} ETH`;

const Row = ({ label, value, sub, testId, strong }) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className="text-slate-400">{label}</span>
    <span className="text-right" data-testid={testId}>
      <span className={strong ? 'text-white font-semibold' : 'text-slate-200'}>{value}</span>
      {sub && <span className="text-slate-500 ml-2">{sub}</span>}
    </span>
  </div>
);

export const DeployCostQuote = ({ quote, loading, error, networkLabel = 'Ethereum' }) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 py-3" data-testid="deploy-quote-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Estimating gas cost…
      </div>
    );
  }
  if (error) return <p className="text-sm text-red-400" data-testid="deploy-quote-error">{error}</p>;
  if (!quote) return null;

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-4 space-y-2 text-sm" data-testid="deploy-cost-quote">
      <div className="flex items-center gap-2 text-slate-300 font-medium mb-1">
        <Fuel className="w-4 h-4 text-amber-300" /> Estimated deployment cost
      </div>
      <Row label="Gas" value={`${quote.gas_units.toLocaleString()} units`} sub={`@ ${quote.gas_price_gwei} gwei`} testId="deploy-quote-gas" />
      <Row label="Estimated cost" value={usd(quote.cost_usd)} sub={eth(quote.cost_eth)} testId="deploy-quote-cost" strong />
      <Row label="Needed in wallet" value={usd(quote.required_usd)} sub={eth(quote.required_eth)} testId="deploy-quote-required" />
      <Row label="Wallet balance" value={usd(quote.wallet_balance_usd)} sub={eth(quote.wallet_balance_eth)} testId="deploy-quote-balance" />
      <p className="text-xs text-slate-500">ETH price {usd(quote.eth_usd)} · includes a 20% buffer for gas price changes</p>
      {quote.sufficient ? (
        <p className="flex items-center gap-2 text-emerald-400 text-xs" data-testid="deploy-quote-sufficient">
          <CheckCircle className="w-4 h-4" /> Wallet has enough ETH for this deployment
        </p>
      ) : (
        <p className="flex items-start gap-2 text-red-400 text-xs" data-testid="deploy-quote-shortfall">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Not enough ETH. Send at least {eth(quote.shortfall_eth)} to {quote.wallet_address} on {networkLabel} before deploying.
        </p>
      )}
    </div>
  );
};

export default DeployCostQuote;
